import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Stack,
  Grid,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { HugeiconsIcon } from '@hugeicons/react';
import Home01Icon from '@hugeicons/core-free-icons/dist/esm/Home01Icon';
import File01Icon from '@hugeicons/core-free-icons/dist/esm/File01Icon';
import UserIcon from '@hugeicons/core-free-icons/dist/esm/UserIcon';
import UserGroupIcon from '@hugeicons/core-free-icons/dist/esm/UserGroupIcon';
import Building01Icon from '@hugeicons/core-free-icons/dist/esm/Building01Icon';
import StarIcon from '@hugeicons/core-free-icons/dist/esm/StarIcon';
import Target01Icon from '@hugeicons/core-free-icons/dist/esm/Target01Icon';
import Mail01Icon from '@hugeicons/core-free-icons/dist/esm/Mail01Icon';
import '../../styles/public-pages/industries.css';

const Industries: React.FC = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>('hospitality-keep');

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const sections = [
    { id: 'hospitality', label: 'Hospitality', emoji: '⭐️' },
    { id: 'retail', label: 'Retail', emoji: '🏠' },
    { id: 'charity', label: 'Charity', emoji: '📨' },
    { id: 'fintech', label: 'Fintech', emoji: '🎯' },
    { id: 'healthcare', label: 'Healthcare', emoji: '👥' },
    { id: 'tech', label: 'Tech', emoji: '🧪' },
  ] as const;

  const industryContent: Record<
    (typeof sections)[number]['id'],
    {
      title: string;
      intro: string;
      accordions: { title: string; body: string }[];
    }
  > = {
    hospitality: {
      title: 'Happy teams create great guest experiences',
      intro:
        'Long hours. High turnover. Staff shortages. Customer-facing pressure. Seasonal peaks that push everyone to their limits. And a workforce that often feels invisible despite being the face of your business.',
      accordions: [
        {
          title: 'Retention that actually works',
          body:
            'Stop the revolving door of recruitment by understanding why good people leave and what makes them stay.',
        },
        {
          title: 'Front-line voices heard',
          body:
            'Your servers, bartenders, housekeepers, and kitchen staff see problems management never sees. We help you tap into that knowledge.',
        },
        {
          title: 'Better team culture',
          body:
            'Create environments where people look out for each other during the rush, not just survive their shifts.',
        },
        {
          title: 'Sustainable workloads',
          body:
            "Identify what's burning people out and build systems that work long-term, not just through the next busy weekend.",
        },
      ],
    },
    retail: {
      title: 'Your shop floor knows what customers really want',
      intro:
        'Demanding customers. Unrealistic targets. Standing all day. Weekend and evening work. Being treated as replaceable while being essential to the business actually running.',
      accordions: [
        {
          title: 'Empowering shop floor teams',
          body:
            "The people on the floor know what customers want, what stock issues exist, and what processes don't work. Let's actually listen to them.",
        },
        {
          title: 'Reducing turnover costs',
          body:
            "Training new staff constantly is expensive. Create a workplace people don't want to leave.",
        },
        {
          title: 'Bridge the HQ-store gap',
          body:
            'Head office makes decisions; stores live with the consequences. We help create better communication and decision-making.',
        },
        {
          title: 'Recognizing contribution',
          body:
            'Move beyond "employee of the month" to genuine recognition and development opportunities.',
        },
      ],
    },
    tech: {
      title: 'Scale without losing what made you special',
      intro:
        'Fast growth. Constant change. Competition for talent. The pressure to ship. Technical debt. Meeting fatigue. The challenge of keeping startup energy as you scale.',
      accordions: [
        {
          title: 'Scale without losing your soul',
          body:
            'Keep what made your culture great as you grow, while evolving what needs to change.',
        },
        {
          title: 'Prevent burnout in high performers',
          body:
            'Your best people are often the ones suffering silently. Catch problems before you lose them.',
        },
        {
          title: 'Cross-team collaboration',
          body:
            'Break down silos between engineering, product, design, and commercial teams.',
        },
        {
          title: 'Inclusive innovation',
          body:
            'Build teams where diverse perspectives drive better solutions, not just check boxes.',
        },
      ],
    },
    fintech: {
      title: 'Move fast and support your people',
      intro:
        'Regulatory pressure. Security concerns. Traditional finance culture meets startup speed. High stakes. The challenge of innovation within constraints. Balancing risk management with moving fast.',
      accordions: [
        {
          title: 'Psychological safety',
          body:
            'People need to flag problems and admit mistakes without fear, especially in finance.',
        },
        {
          title: 'Bridging tech and finance cultures',
          body:
            'Your developers and your compliance team see the world differently. Help them work together effectively.',
        },
        {
          title: 'Innovation within structure',
          body:
            'Create space for creativity and fresh thinking while maintaining necessary controls.',
        },
        {
          title: 'Retention in competitive markets',
          body:
            "Everyone's trying to hire your best people. Make sure they have reasons to stay beyond salary.",
        },
      ],
    },
    healthcare: {
      title: 'Supporting the people who provide care',
      intro:
        'Emotional exhaustion. Understaffing. Bureaucracy that gets in the way of patient care. Moral injury from being unable to provide the care you were trained to give. Shift work. Life and death stakes.',
      accordions: [
        {
          title: 'Reduce fatigue',
          body:
            'Create support systems that help your people sustain the emotional demands of care work.',
        },
        {
          title: 'Improve communication',
          body:
            "Doctors, nurses, admin, porters—everyone's essential, but hierarchies and silos create friction.",
        },
        {
          title: 'Address systemic frustrations',
          body:
            'Surface the bureaucratic barriers that prevent good care and waste your staff\'s energy.',
        },
        {
          title: 'Retain experienced staff',
          body:
            "You've invested years in training. Keep the expertise and wisdom in your organization.",
        },
      ],
    },
    charity: {
      title: 'Passion needs sustainable systems to create lasting impact',
      intro:
        'Doing more with less. Passion that sometimes covers for poor systems. Mission-driven teams who care deeply but risk burnout. Funding uncertainty. The pressure to justify every penny to donors.',
      accordions: [
        {
          title: 'Sustainable impact',
          body:
            'Your mission is long-term. Your team needs to be too. Build systems that prevent burnout.',
        },
        {
          title: 'Engagement and retention',
          body:
            "Make volunteers feel valued and effective, not just grateful you accepted their help.",
        },
        {
          title: 'Align mission and operations',
          body:
            'Sometimes internal practices contradict your external values. Let\'s address that honestly.',
        },
        {
          title: 'Resource prioritisation',
          body:
            'Your people know where resources would make the biggest difference. Listen to them.',
        },
      ],
    },
  };

  const industryImages: Record<(typeof sections)[number]['id'], string> = {
    hospitality:
      'https://images.unsplash.com/photo-1507914372368-b2b085b925a1?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    retail:
      'https://images.unsplash.com/photo-1601924994987-69e26d50dc26?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    charity:
      'https://plus.unsplash.com/premium_photo-1683140681865-9f901073f2a2?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    fintech:
      'https://plus.unsplash.com/premium_photo-1661610875787-1c91e859cba2?q=80&w=1808&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    healthcare:
      'https://plus.unsplash.com/premium_photo-1661766708050-a164431ffdf5?q=80&w=1742&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    tech:
      'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(`industry-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box className="industries-page" sx={{ bgcolor: 'background.default' }}>
      {/* Hero (copied from Platform) */}
      <Box
        className="platform-hero industries-hero"
        sx={{
          pt: { xs: 12, md: 16 },
          pb: { xs: 10, md: 12 },
          background:
            'radial-gradient(1200px 600px at 50% 15%, rgba(0,67,254,0.18) 0%, rgba(3,163,255,0.08) 45%, rgba(255,255,255,1) 75%)',
        }}
      >
        <Container className="slider-container" maxWidth="lg">
          <Box className="platform-hero__content" sx={{ textAlign: 'center', maxWidth: 920, mx: 'auto' }}>
            <Typography
              className="platform-hero__title"
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.02em',
                fontSize: { xs: 36, sm: 44, md: 56 },
                lineHeight: 1.1,
              }}
            >
              People-powered insights for any industry
            </Typography>
            <Typography
              className="platform-hero__subtitle"
              variant="h6"
              color="text.secondary"
              sx={{ mt: 2, maxWidth: 820, mx: 'auto' }}
            >
              Explore how SuperGauge supports organisations across hospitality and beyond—keeping teams engaged,
              supported and ready to deliver their best work.
            </Typography>
            <Stack
              className="industries-hero-tabs"
              direction="row"
              justifyContent="center"
              spacing={1.5}
              sx={{ mt: 4, flexWrap: 'wrap' }}
            >
              {sections.map((section) => (
                <Button
                  key={section.id}
                  className="industries-hero-tab"
                  variant="text"
                  onClick={() => scrollToSection(section.id)}
                >
                  <span className="industries-hero-tab__emoji" aria-hidden="true">
                    {section.emoji}
                  </span>
                  <span className="industries-hero-tab__label">{section.label}</span>
                </Button>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Industry accordion sections */}
      {sections.map((section) => (
        <Box
          key={section.id}
          id={`industry-${section.id}`}
          className="industries-accordion"
          sx={{ py: { xs: 8, md: 10 } }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="stretch">
              <Grid item xs={12} md={6} className="industries-accordion__left">
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 700, letterSpacing: 0.5, color: 'primary.main', mb: 1 }}
                >
                  {section.label}
                </Typography>
                <Typography
                  variant="h4"
                  className="industries-accordion__title"
                  sx={{ fontWeight: 800, mb: 2, lineHeight: 1.1 }}
                >
                  {industryContent[section.id].title}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {industryContent[section.id].intro}
                </Typography>

                <Box className="industries-accordion__list">
                  <Accordion
                    expanded={expanded === `${section.id}-keep`}
                    onChange={handleChange(`${section.id}-keep`)}
                    className="industries-accordion__item"
                  >
                    <AccordionSummary
                      expandIcon={<Box className="industries-accordion__plus">+</Box>}
                      className="industries-accordion__summary"
                    >
                      <Box className="industries-accordion__icon">
                        <HugeiconsIcon icon={UserIcon as any} size={18} strokeWidth={1.8} />
                      </Box>
                      <Typography className="industries-accordion__item-title">
                        {industryContent[section.id].accordions[0].title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography className="industries-accordion__item-body">
                        {industryContent[section.id].accordions[0].body}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={expanded === `${section.id}-frontline`}
                    onChange={handleChange(`${section.id}-frontline`)}
                    className="industries-accordion__item"
                  >
                    <AccordionSummary
                      expandIcon={<Box className="industries-accordion__plus">+</Box>}
                      className="industries-accordion__summary"
                    >
                      <Box className="industries-accordion__icon">
                        <HugeiconsIcon icon={Target01Icon as any} size={18} strokeWidth={1.8} />
                      </Box>
                      <Typography className="industries-accordion__item-title">
                        {industryContent[section.id].accordions[1].title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography className="industries-accordion__item-body">
                        {industryContent[section.id].accordions[1].body}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={expanded === `${section.id}-culture`}
                    onChange={handleChange(`${section.id}-culture`)}
                    className="industries-accordion__item"
                  >
                    <AccordionSummary
                      expandIcon={<Box className="industries-accordion__plus">+</Box>}
                      className="industries-accordion__summary"
                    >
                      <Box className="industries-accordion__icon">
                        <HugeiconsIcon icon={UserGroupIcon as any} size={18} strokeWidth={1.8} />
                      </Box>
                      <Typography className="industries-accordion__item-title">
                        {industryContent[section.id].accordions[2].title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography className="industries-accordion__item-body">
                        {industryContent[section.id].accordions[2].body}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={expanded === `${section.id}-workloads`}
                    onChange={handleChange(`${section.id}-workloads`)}
                    className="industries-accordion__item"
                  >
                    <AccordionSummary
                      expandIcon={<Box className="industries-accordion__plus">+</Box>}
                      className="industries-accordion__summary"
                    >
                      <Box className="industries-accordion__icon">
                        <HugeiconsIcon icon={StarIcon as any} size={18} strokeWidth={1.8} />
                      </Box>
                      <Typography className="industries-accordion__item-title">
                        {industryContent[section.id].accordions[3].title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography className="industries-accordion__item-body">
                        {industryContent[section.id].accordions[3].body}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} className="industries-accordion__right">
                <Box
                  className="industries-accordion__visual"
                  sx={{
                    backgroundImage: `url(${industryImages[section.id]})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
              </Grid>
            </Grid>
          </Container>
        </Box>
      ))}

      {/* Site footer (shared with landing/platform pages) */}
      <Box className="site-footer" sx={{ pt: 10, pb: 4, bgcolor: '#000430', color: '#ffffff' }}>
        {/* Middle nav columns */}
        <Container className="site-footer__links" maxWidth="lg" sx={{ mt: 8 }}>
          <Grid className="site-footer__grid" container spacing={6}>
            <Grid className="site-footer__brand" item xs={12} md={4}>
              <Box
                component="img"
                className="site-footer__logo"
                src={`${process.env.PUBLIC_URL}/sglogo.svg`}
                alt="SuperGauge"
                sx={{ height: 32, width: 'auto', display: 'block', mb: 1.5 }}
              />
              <Typography
                className="site-footer__branddesc"
                variant="body2"
                sx={{ mt: 1.5, color: 'rgba(255,255,255,0.8)' }}
              >
                Gather the insights you need to make people-driven, data-informed decisions about your company culture.
              </Typography>
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* Reserved for future footer nav links */}
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* Reserved for future footer nav links */}
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* Reserved for future footer nav links */}
            </Grid>
          </Grid>
        </Container>

        {/* Bottom bar */}
        <Container className="site-footer__bar" maxWidth="lg" sx={{ mt: 6 }}>
          <Box
            className="site-footer__bar-inner"
            sx={{ borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.2)', pt: 2 }}
          >
            <Grid className="site-footer__bar-grid" container alignItems="center">
              <Grid className="site-footer__bar-left" item xs={12} md={6}>
                <Typography className="site-footer__copyright" variant="caption">
                  © {new Date().getFullYear()} SuperGauge
                </Typography>
              </Grid>
              <Grid className="site-footer__bar-right" item xs={12} md={6}>
                <Box className="site-footer__contact" sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography className="site-footer__link" variant="caption">
                    Contact
                  </Typography>
                  <Typography className="site-footer__link" variant="caption">
                    Terms
                  </Typography>
                  <Typography className="site-footer__link" variant="caption">
                    Privacy policy
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Industries;
